import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft, FaDownload, FaExclamationTriangle, 
  FaInfoCircle, FaArrowRight, FaCloudSun, FaSeedling, 
  FaTint, FaRupeeSign, FaBoxOpen, FaFilePdf 
} from 'react-icons/fa';
import './RiskResult.css';

const RiskResult = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = i18n.language || 'mr';
  const isHindi = currentLang === 'hi';
  const isMarathi = currentLang === 'mr';

  const [assessment, setAssessment] = useState(null);

  useEffect(() => {
    if (location.state && location.state.assessment) {
      setAssessment(location.state.assessment);
      return;
    }

    const cached = localStorage.getItem('lastDistressAssessment');
    if (cached) {
      try {
        setAssessment(JSON.parse(cached));
        return;
      } catch (e) {
        console.error(e);
      }
    }

    // Single source of truth synchronized with 72 Cockpit DRI
    setAssessment({
      crop: 'Banana',
      dri_score: 72,
      level: 'HIGH',
      level_localized: isHindi ? 'उच्च जोखिम' : (isMarathi ? 'उच्च धोका' : 'HIGH RISK'),
      status_message: isHindi 
        ? 'आपकी फसल उच्च जोखिम में है। समय रहते कदम उठाएं और नुकसान से बचें।'
        : (isMarathi ? 'तुमचे पीक उच्च धोक्यात आहे. वेळेवर पावले उचला आणि नुकसान टाळा.' : 'Your crop is under high distress. Take action now to avoid potential losses.'),
      factors: [
        { id: 'weather', name: isHindi ? 'मौसम' : (isMarathi ? 'हवामान' : 'Weather'), icon: 'weather', impact_pct: 25, reason_hi: 'बारिश अपेक्षित से कम है', reason_mr: 'अपेक्षित पावसापेक्षा कमी पाऊस', reason_en: 'Rainfall below expected level', score: 75, level: 'उच्च' },
        { id: 'water', name: isHindi ? 'पानी' : (isMarathi ? 'पाणी' : 'Water'), icon: 'water', impact_pct: 22, reason_hi: 'सिंचाई उपलब्धता सीमित है', reason_mr: 'सिंचनाची उपलब्धता मर्यादित', reason_en: 'Low irrigation availability', score: 70, level: 'उच्च' },
        { id: 'market', name: isHindi ? 'मार्केट' : (isMarathi ? 'बाजार' : 'Market'), icon: 'market', impact_pct: 18, reason_hi: 'कीमत गिर रही है (MSP से कम)', reason_mr: 'भाव घसरत आहेत (MSP पेक्षा कमी)', reason_en: 'Price below MSP & falling trend', score: 68, level: 'उच्च' },
        { id: 'soil', name: isHindi ? 'मिट्टी' : (isMarathi ? 'माती' : 'Soil'), icon: 'soil', impact_pct: 16, reason_hi: 'मिट्टी में नमी कम है', reason_mr: 'मातीत ओलावा कमी आहे', reason_en: 'Low soil moisture stress', score: 45, level: 'मध्यम' },
        { id: 'spoilage', name: isHindi ? 'पोस्ट-हार्वेस्ट' : (isMarathi ? 'कापणीनंतर' : 'Spoilage'), icon: 'spoilage', impact_pct: 7, reason_hi: 'भंडारण स्थिति मध्यम', reason_mr: 'साठवणूक जोखीम मध्यम', reason_en: 'Moderate storage risk', score: 35, level: 'मध्यम' }
      ]
    });
  }, [location.state, currentLang, isHindi, isMarathi]);

  const dri = assessment?.dri_score || 72;
  const factors = assessment?.factors || [];

  const handleDownloadReport = () => {
    window.print();
  };

  return (
    <div className="fd-result-page">
      <div className="fd-result-container">
        {/* Top Header Row */}
        <div className="fd-result-header-row">
          <Link to="/dashboard" className="fd-result-back-btn">
            <FaArrowLeft />
            <span>{isHindi ? 'वापस' : 'Back to Cockpit'}</span>
          </Link>
          <button className="fd-download-btn" onClick={handleDownloadReport}>
            <FaFilePdf />
            <span>{isHindi ? 'रिपोर्ट डाउनलोड करें 📥' : 'Download PDF Report 📥'}</span>
          </button>
        </div>

        {/* Big Overall Risk Result Card */}
        <div className="fd-result-score-card fd-card">
          <div className="fd-score-card-top">
            <div className="fd-score-display">
              <span className="fd-big-dri">{dri}</span>
              <span className="fd-dri-total">/100</span>
            </div>
            <div className="fd-score-badge-box">
              <span className="fd-badge-high">
                <FaExclamationTriangle />
                <span>{isHindi ? 'उच्च जोखिम' : 'HIGH RISK'}</span>
              </span>
            </div>
          </div>

          <div className="fd-severity-alert-banner">
            <p>
              {assessment?.status_message || (isHindi 
                ? 'आपकी स्थिति गंभीर है! जल्दी कार्रवाई करें ताकि नुकसान से बचा जा सके।'
                : 'Your crop situation is critical! Take timely intervention.')}
            </p>
          </div>
        </div>

        {/* Why Is My Risk High? (Glass-Box Explanation Hub) */}
        <div className="fd-why-section-card fd-card">
          <div className="fd-why-header">
            <h3>{isHindi ? 'क्यों उच्च है? (फैक्टर के अनुसार प्रभाव)' : 'Why is your risk high?'}</h3>
            <span className="fd-why-sub">{isHindi ? 'प्रत्येक पैरामीटर का डिस्ट्रेस में योगदान' : 'Factor contributions to DRI score'}</span>
          </div>

          <div className="fd-factors-table">
            {factors.map((f) => (
              <div key={f.id} className="fd-factor-row">
                <div className="fd-factor-col-info">
                  <div className="fd-factor-avatar">
                    {f.id === 'weather' && '🌦️'}
                    {f.id === 'water' && '💧'}
                    {f.id === 'market' && '₹'}
                    {f.id === 'soil' && '🌱'}
                    {f.id === 'spoilage' && '📦'}
                  </div>
                  <div>
                    <h4 className="fd-f-title">{f.name}</h4>
                    <p className="fd-f-reason">{isHindi ? f.reason_hi : f.reason_en}</p>
                  </div>
                </div>

                <div className="fd-factor-col-bar">
                  <div className="fd-progress-track">
                    <div 
                      className={`fd-progress-fill ${f.impact_pct >= 20 ? 'red' : (f.impact_pct >= 10 ? 'amber' : 'green')}`}
                      style={{ width: `${f.impact_pct * 3}%` }}
                    ></div>
                  </div>
                  <span className="fd-impact-text">{f.impact_pct}%</span>
                </div>
              </div>
            ))}
          </div>

          {/* Info callout */}
          <div className="fd-why-info-banner">
            <FaInfoCircle className="fd-info-icon" />
            <p>
              {isHindi 
                ? 'इन 5 फैक्टर्स का संयुक्त प्रभाव आपकी फसल के लिए उच्च जोखिम बना रहा है।'
                : 'These 5 factors are mathematically combined into your Distress Risk Index (DRI).'}
            </p>
          </div>

          {/* Forward CTA to Interventions */}
          <div className="fd-forward-action-bar">
            <Link to="/interventions" className="fd-btn-primary fd-action-next-btn">
              <span>{isHindi ? 'तुरंत क्या करें देखें (What Should I Do Now?)' : 'What Should I Do Now? →'}</span>
              <FaArrowRight />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskResult;
